import { SentBySource } from "../../../application/pages/models/revenue-attribution/RevenueAttributionDto";
export const sentBySourceBadge = (source) => {
    switch (source) {
        case SentBySource.Confirmed:
            return {
                label: "Confirmed",
                tooltip: "This person is credited based on an actual record of when they sent this offer.",
                color: "success",
                needsReview: false,
            };
        case SentBySource.ManualOverride:
            return {
                label: "Manually set",
                tooltip: "An administrator manually corrected who is credited for this offer.",
                color: "info",
                needsReview: false,
            };
        case SentBySource.Inferred:
            return {
                // Deliberately the SAME label as LeadOwnerFallback below: a first-time viewer only needs
                // "trust this or check it" — the technical fallback tier is detail for the tooltip, not
                // something worth two different badge texts for.
                label: "Needs review",
                tooltip: "We couldn't find a direct send record, so this was worked out from the quote email. Please double-check.",
                color: "warning",
                needsReview: true,
            };
        case SentBySource.LeadOwnerFallback:
            return {
                label: "Needs review",
                tooltip: "No send record or email was found, so this was defaulted to the lead's owner. Please double-check.",
                color: "warning",
                needsReview: true,
            };
        default:
            // Includes 0, which maps to no backend enum member and means a row escaped the migration's
            // classification. Surfaced as unknown rather than silently rendered as trustworthy.
            return {
                label: "Unknown",
                tooltip: "The origin of this attribution could not be determined.",
                color: "default",
                needsReview: true,
            };
    }
};
/**
 * This screen intentionally lists both signed and unsigned offers: revenue and "objects won"
 * only ever come from signed contracts, but the "objects sent" leaderboard counts every contract
 * regardless of signing, so correcting SentBy on an unsigned offer matters immediately even
 * though it hasn't earned revenue yet. This badge makes that distinction visible per row instead
 * of leaving it as an invisible assumption.
 */
export const signedStatusBadge = (isSigned) => isSigned
    ? {
        label: "Signed",
        tooltip: "The customer has signed this offer. It counts toward revenue and won-object figures.",
        color: "success",
    }
    : {
        label: "Not signed",
        tooltip: "The customer has not signed this offer yet. It counts toward objects-sent figures, but not yet toward revenue or won-object figures.",
        color: "default",
    };
// Sentinel dropdown values that are NOT raw SentBySource enum members — they each map to a
// derived server-side filter instead (see RevenueAttributionSearchRequestDto).
export const UNATTRIBUTED_FILTER = "unattributed";
export const NEEDS_REVIEW_FILTER = "needs-review";
export const ALL_SOURCES_FILTER = "all";
/** The dropdown's current selection, derived from filter state — the three flags are mutually exclusive. */
export const sourceFilterDropdownValue = (state) => {
    if (state.onlyUnattributed)
        return UNATTRIBUTED_FILTER;
    if (state.needsReview)
        return NEEDS_REVIEW_FILTER;
    if (state.sentBySource)
        return state.sentBySource;
    return ALL_SOURCES_FILTER;
};
/**
 * Resolves a dropdown selection back into filter state. Centralised here (rather than inline
 * ternaries in the component) because the mutual exclusivity between "all", "not attributed",
 * "needs review", and a specific confirmed/manual source is easy to get subtly wrong once there
 * are four cases instead of three.
 */
export const applySourceFilterSelection = (value) => {
    if (value === UNATTRIBUTED_FILTER)
        return { sentBySource: null, onlyUnattributed: true, needsReview: false };
    if (value === NEEDS_REVIEW_FILTER)
        return { sentBySource: null, onlyUnattributed: false, needsReview: true };
    if (value === ALL_SOURCES_FILTER)
        return { sentBySource: null, onlyUnattributed: false, needsReview: false };
    return { sentBySource: Number(value), onlyUnattributed: false, needsReview: false };
};
/**
 * Whether the Save button should be enabled. Blocks the no-op save (same user re-selected) so the
 * user gets no misleading "saved" toast and the server is not asked to write a change that isn't one.
 */
export const canSaveSentBy = (row, selectedUserId, saving) => {
    if (!row || saving)
        return false;
    if (!selectedUserId)
        return false;
    return selectedUserId !== row.sentBy;
};
/** Display name for the currently credited user, or a neutral dash when there is none. */
export const currentSenderLabel = (row) => {
    const name = ((row === null || row === void 0 ? void 0 : row.sentByName) || "").trim();
    return name !== "" ? name : "-";
};
